import React from 'react'
import { Box, Overlay } from '@mantine/core'
import { IconTrash } from '@tabler/icons-react'
import { useEditMode } from './providers'
import { clsx } from 'clsx'
import classes from './ActionOverlay.module.css'
import { useWidgetEditor } from './state'

interface ActionOverlayProps {
  widgetId: string
  container: boolean
}

export function ActionOverlay({ widgetId, container }: ActionOverlayProps) {
  const [editMode] = useEditMode()
  const [isHovered, setIsHovered] = React.useState(false)
  const editor = useWidgetEditor()

  const editingWidget = editor.useEditWidgetId()
  const mode = editor.useMode()

  const editingWidgetConfig = editor.useEditConfig()

  // Determine if this widget is a child of the editing widget
  const isChild = React.useMemo(() => {
    if (!editingWidget || !editingWidgetConfig) return false
    return editingWidgetConfig.children?.includes(widgetId) || false
  }, [editingWidget, editingWidgetConfig, widgetId])

  const isBeingEdited = editingWidget === widgetId

  // Determine overlay type and className
  const getOverlayConfig = () => {
    // If not in edit mode, no overlay
    if (!editMode) return null

    // If nothing is being edited, show hover effect
    if (!editingWidget) {
      return isHovered
        ? { type: 'hover', className: classes.hoverOverlay }
        : null
    }

    switch (mode) {
      case 'options':
      case 'add':
        if (isBeingEdited) {
          return { type: 'edit', className: classes.editOverlay }
        }
        break

      case 'remove':
        if (isChild) {
          return {
            type: 'remove',
            className: classes.removeOverlay,
            showTrash: true,
          }
        }
        break

      case 'reorder':
        if (isChild) {
          return { type: 'reorder', className: classes.reorderOverlay }
        }
        break
    }

    return null
  }

  const overlayConfig = getOverlayConfig()

  const handleOverlayClick = React.useCallback(() => {
    if (!editingWidget && editMode) {
      editor.startEditWidget(widgetId, 'options')
    } else if (mode === 'remove' && isChild) {
      editor.removeChild(widgetId)
    }
  }, [editingWidget, editMode, mode, isChild, widgetId, editor])

  if (!editMode) return null
  if (container && !isChild) return null

  return (
    <Overlay
      className={clsx(classes.overlayBox, overlayConfig?.className)}
      opacity={1}
      style={{ cursor: 'pointer' }}
      onClick={handleOverlayClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {overlayConfig?.showTrash && (
        <Box className={classes.trashIcon}>
          <IconTrash size={20} color="#fa5252" />
        </Box>
      )}
    </Overlay>
  )
}
